import { Check, ListFilter } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { useListBranchQuery } from "@/services/backend/company/api/branch";
import t from "@/lang/lang";
import { Filter } from "@/shared/lib/filter/filter";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Spinner } from "@/shared/components/ui/spinner";

export interface BranchFilterProps {
  filter: Filter<string> | undefined;
  onFilterChange: (filter: Filter<string>) => void;
  companyIds?: string[];
  hidden?: boolean;
}

export function BranchFilter({
  filter,
  onFilterChange,
  companyIds,
  hidden = false,
}: BranchFilterProps) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };
  // simplifies some code below
  const currentValues = filterVal.values ?? [];

  // We use the active company if no more specific companyId is given.
  // This is a very common case and saves looking up the active company.
  const { companyId: activeCompany } = useActiveCompany();

  const {
    data: branchList,
    isLoading,
    error,
  } = useListBranchQuery({
    // The active company is only used if no companyIds are given
    company: { active: true, values: [...(companyIds ?? [activeCompany])] },
  });

  if (hidden) {
    return null;
  }

  // We hide this for users with only one branch
  if (branchList && branchList.branches.length <= 1) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ListFilter />
          {t("Niederlassungen")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Suchen")} />
          {isLoading && (
            <span className="flex w-full items-center justify-center gap-2 py-3 text-sm">
              <Spinner />
              {t("Lade Daten ...")}
            </span>
          )}
          {error && (
            <RTKQueryErrorAlert error={error} className="border-none" />
          )}
          {branchList && (
            <CommandList>
              <CommandEmpty className="py-3 text-center text-sm">
                {branchList.branches.length === 0
                  ? t("Keine Niederlassungen")
                  : t("Nicht gefunden")}
              </CommandEmpty>
              <CommandGroup>
                {branchList.branches.map((branch) => (
                  <CommandItem
                    key={branch.id}
                    value={branch.name}
                    onSelect={() => {
                      const values = currentValues.includes(branch.id)
                        ? currentValues.filter((v) => v !== branch.id)
                        : [...currentValues, branch.id];
                      onFilterChange({
                        active: values.length > 0,
                        values,
                      });
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        currentValues.includes(branch.id)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {branch.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
